import React, { useMemo, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../Context/AuthContext';
import { GetCountries, GetState } from 'react-country-state-city';
import { getAllProviderSettings } from '../../Services/ProviderService';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import DocumentUpload from '../Common/DocumentUpload';
import CustomDatePicker from '../Common/CustomDatePicker';
import { toast } from 'react-toastify';

const validationSchema = Yup.object({
  license: Yup.string().trim().required('License number is required'),
  license_state: Yup.string().trim().nullable(),
  is_multi_state: Yup.boolean().nullable(),
  is_primary_license: Yup.boolean().nullable(),
  issue_date: Yup.date().nullable().typeError('Invalid date'),
  expiration_date: Yup.date().nullable().typeError('Invalid date'),
  license_status: Yup.number().nullable(),
  provider_license_type: Yup.number().nullable(),
  taxonomy_code: Yup.number().nullable(),
  fee_exemption: Yup.number().nullable(),
  enrolled_prescription_drug_program: Yup.boolean().nullable(),
  additional_info: Yup.boolean().nullable(),
  board_action: Yup.string().trim().nullable(),
});

const emptyForm = {
  id: null,
  provider_id: null,
  status_id: null,
  file_id: null,
  license_type: null,
  license_state: '',
  is_multi_state: null,
  license: '',
  is_primary_license: null,
  issue_date: null,
  expiration_date: null,
  license_status: '',
  provider_license_type: '',
  taxonomy_code: '',
  fee_exemption: '',
  enrolled_prescription_drug_program: null,
  additional_info: null,
  board_action: '',
};

const LICENSE_STATUS_SETTING_ID = 53;
const TAXONOMY_CODE_SETTING_ID = 37;
const PROVIDER_LICENSE_TYPE_SETTING_ID = 20;

const mapProviderSettingItem = (item) => ({
  value: item.id ?? item.Id ?? item.value ?? item.value_id ?? null,
  label: item.name ?? item.Name ?? item.code ?? String(item.id ?? ''),
});

export default function LicensureModal({ isOpen, onClose, onSave, initialData, isLoading, categoryLabel, licenseType, providerId }) {
  const { token } = useAuth();
  const [providerSettings, setProviderSettings] = useState({
    license_status: [],
    provider_license_type: [],
    taxonomy_code: [],
  });
  const [licenseStateOptions, setLicenseStateOptions] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const loadProviderSettings = async () => {
    if (!token) return;
    try {
      setSettingsLoading(true);
      const entries = [
        ['license_status', LICENSE_STATUS_SETTING_ID],
        ['provider_license_type', PROVIDER_LICENSE_TYPE_SETTING_ID],
        ['taxonomy_code', TAXONOMY_CODE_SETTING_ID],
      ];
      const results = await Promise.all(
        entries.map(async ([key, typeId]) => {
          try {
            const response = await getAllProviderSettings(token, typeId);
            if (response?.is_success && Array.isArray(response.data)) {
              return { key, options: response.data.map(mapProviderSettingItem) };
            } else if (response?.is_success === false && response?.status_code === 500) {
              toast.error('Internal Server Error. Please Contact Support Team.');
              console.error(`Failed to load provider settings for ${key}:`, response?.message || 'Unknown error');
            } else {
              toast.error(response?.message || `Failed to load settings`);
              console.error(`Failed to load provider settings for ${key}:`, response?.message || 'Unknown error');
            }
          } catch (error) {
            console.error(`Failed to load provider settings for ${key}:`, error);
          }
          return { key, options: [] };
        })
      );
      setProviderSettings(results.reduce((acc, item) => ({ ...acc, [item.key]: item.options }), {}));
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadLicenseStateOptions = async () => {
    try {
      const countries = await GetCountries();
      const unitedStates = Array.isArray(countries)
        ? countries.find((country) =>
            ['united states', 'us', 'usa'].includes(String(country.name ?? '').trim().toLowerCase()) ||
            String(country.iso2 ?? '').toUpperCase() === 'US' ||
            String(country.iso3 ?? '').toUpperCase() === 'USA'
          )
        : null;
      const countryId = unitedStates?.id ?? unitedStates?.iso2 ?? unitedStates?.iso3 ?? 'US';
      const stateList = await GetState(countryId);
      if (Array.isArray(stateList)) {
        setLicenseStateOptions(
          stateList.map((state) => ({
            value: state.name ?? state.state_code ?? state.code ?? '',
            label: state.name ?? state.state_code ?? state.code ?? '',
            key: state.id ?? state.state_code ?? state.name,
          }))
        );
      }
    } catch (error) {
      console.error('Failed to load license state options:', error);
      setLicenseStateOptions([]);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadProviderSettings();
    loadLicenseStateOptions();
  }, [token]);
  const initialValues = useMemo(
    () => ({
      ...emptyForm,
      ...initialData,
      license_type: licenseType ?? initialData?.license_type,
      provider_id: providerId ?? initialData?.provider_id,
      document: initialData?.document ?? initialData?.file_name ?? initialData?.fileName ?? null,
    }),
    [initialData, licenseType, providerId],
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        id: values.id || null,
        license_status: values.license_status !== '' && values.license_status !== null && values.license_status !== undefined ? Number(values.license_status) : null,
        provider_license_type: values.provider_license_type !== '' && values.provider_license_type !== null && values.provider_license_type !== undefined ? Number(values.provider_license_type) : null,
        taxonomy_code: values.taxonomy_code !== '' && values.taxonomy_code !== null && values.taxonomy_code !== undefined ? Number(values.taxonomy_code) : null,
        fee_exemption: values.fee_exemption !== '' && values.fee_exemption !== null && values.fee_exemption !== undefined ? Number(values.fee_exemption) : null,
        is_multi_state: values.is_multi_state === true || values.is_multi_state === 'true' ? true : values.is_multi_state === false || values.is_multi_state === 'false' ? false : null,
        is_primary_license: values.is_primary_license === true || values.is_primary_license === 'true' ? true : values.is_primary_license === false || values.is_primary_license === 'false' ? false : null,
        enrolled_prescription_drug_program: values.enrolled_prescription_drug_program === true || values.enrolled_prescription_drug_program === 'true' ? true : values.enrolled_prescription_drug_program === false || values.enrolled_prescription_drug_program === 'false' ? false : null,
        additional_info: values.additional_info === true || values.additional_info === 'true' ? true : values.additional_info === false || values.additional_info === 'false' ? false : null,
        issue_date: values.issue_date || null,
        expiration_date: values.expiration_date || null,
        type: values.id ? 'update' : 'create',
      };
      onSave(payload);
    },
  });

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block m-0" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content border-0 rounded-4">
          <div className="modal-header border-bottom-0 px-4 py-2 d-flex justify-content-between align-items-center">
            <h5 className="modal-title fw-bold hc-text-primary m-0">{categoryLabel} License</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isLoading} />
          </div>
          <form onSubmit={formik.handleSubmit}>
            <input type="hidden" {...formik.getFieldProps('license_type')} />
            <input type="hidden" {...formik.getFieldProps('provider_id')} />
            <div className="modal-body px-4 py-2">
              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">License Details</h6>
                <div className="row g-3">
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">License Number</label>
                    <input
                      {...formik.getFieldProps('license')}
                      className={`form-control rounded-3 shadow-sm ${formik.touched.license && formik.errors.license ? 'is-invalid' : ''}`}
                      placeholder="Enter license number"
                    />
                    <div className="invalid-feedback">{formik.errors.license}</div>
                  </div>

                  <div className="col-lg-6">
                    <SearchableDropdown
                      label="License State"
                      placeholder="Select Option"
                      options={licenseStateOptions}
                      value={formik.values.license_state || ''}
                      onChange={(value) => formik.setFieldValue('license_state', value || '')}
                      disabled={settingsLoading && !licenseStateOptions.length}
                      loading={settingsLoading && !licenseStateOptions.length}
                      emptyMessage="No license states found"
                      allowCustom={true}
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Is Multi-State</label>
                    <select
                      {...formik.getFieldProps('is_multi_state')}
                      className="form-select rounded-3 shadow-sm"
                    >
                      <option value="">Select Option</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Primary License</label>
                    <select
                      {...formik.getFieldProps('is_primary_license')}
                      className="form-select rounded-3 shadow-sm"
                    >
                      <option value="">Select Option</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Dates & Status</h6>
                <div className="row g-3">
                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">Issue Date</label>
                    <CustomDatePicker
                    formik={formik}
                    name="issue_date"
                    />
                    {/* <input
                      type="date"
                      {...formik.getFieldProps('issue_date')}
                      className="form-control rounded-3 shadow-sm"
                    /> */}
                  </div>

                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">Expiration Date</label>
                    <CustomDatePicker
                      formik={formik}
                      name="expiration_date"
                    />
                  </div>

                  <div className="col-lg-4">
                    <SearchableDropdown
                      label="License Status"
                      placeholder="Select Option"
                      options={providerSettings.license_status}
                      value={formik.values.license_status ?? ''}
                      onChange={(value) => formik.setFieldValue('license_status', value ?? '')}
                      disabled={settingsLoading && !providerSettings.license_status.length}
                      loading={settingsLoading && !providerSettings.license_status.length}
                      emptyMessage="No license statuses found"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Identifier Details</h6>
                <div className="row g-3">
                  <div className="col-lg-4">
                    <SearchableDropdown
                      label="Provider License Type"
                      placeholder="Select Option"
                      options={providerSettings.provider_license_type}
                      value={formik.values.provider_license_type ?? ''}
                      onChange={(value) => formik.setFieldValue('provider_license_type', value ?? '')}
                      disabled={settingsLoading && !providerSettings.provider_license_type.length}
                      loading={settingsLoading && !providerSettings.provider_license_type.length}
                      emptyMessage="No provider license types found"
                    />
                  </div>

                  <div className="col-lg-4">
                    <SearchableDropdown
                      label="Taxonomy Code"
                      placeholder="Select Option"
                      options={providerSettings.taxonomy_code}
                      value={formik.values.taxonomy_code ?? ''}
                      onChange={(value) => formik.setFieldValue('taxonomy_code', value ?? '')}
                      disabled={settingsLoading && !providerSettings.taxonomy_code.length}
                      loading={settingsLoading && !providerSettings.taxonomy_code.length}
                      emptyMessage="No taxonomy codes found"
                    />
                  </div>

                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">Fee Exemption</label>
                    <input
                      {...formik.getFieldProps('fee_exemption')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter fee exemption"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Program & Additional Info</h6>
                <div className="row g-3">
                  <div className="col-lg-6 col-xl-4">
                    <label className="form-label fw-semibold">Enrolled Prescription Drug Program</label>
                    <select
                      {...formik.getFieldProps('enrolled_prescription_drug_program')}
                      className="form-select rounded-3 shadow-sm"
                    >
                      <option value="">Select Option</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  <div className="col-lg-6 col-xl-4">
                    <label className="form-label fw-semibold">Additional Info</label>
                    <select
                      {...formik.getFieldProps('additional_info')}
                      className="form-select rounded-3 shadow-sm"
                    >
                      <option value="">Select Option</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  <div className="col-lg-12 col-xl-4">
                    <label className="form-label fw-semibold">Board Action</label>
                    <input
                      {...formik.getFieldProps('board_action')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter board action"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Document</h6>
                <DocumentUpload
                  id="licensure-document-input"
                  label="Upload Document"
                  value={formik.values.document}
                  onChange={(file) => formik.setFieldValue('document', file)}
                />
              </div>
            </div>
            <div className="modal-footer border-top-0 p-4 d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-light rounded-3 px-4" onClick={onClose} disabled={isLoading}>Cancel</button>
              <button type="submit" className="btn hc-bg-primary text-white rounded-3 px-4 d-flex align-items-center justify-content-center gap-2" disabled={isLoading} aria-busy={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
